/** EDP-00005 — Enterprise Human Experience framework (mirrors pkg/hx) */

export const EDP_HX_ID = 'EDP-00005';

export type Philosophy =
  | 'goal_first'
  | 'task_before_screen'
  | 'business_before_technology'
  | 'context_before_action'
  | 'ai_assists_human_decides'
  | 'minimal_cognitive_load'
  | 'progressive_disclosure'
  | 'explain_before_execute'
  | 'learn_through_doing'
  | 'predict_user_intent';

export type UserGoal =
  | 'launch_product'
  | 'improve_listing'
  | 'increase_revenue'
  | 'reduce_acos'
  | 'improve_roas'
  | 'generate_images'
  | 'publish_a_plus'
  | 'fix_listing_errors'
  | 'increase_reviews'
  | 'improve_inventory'
  | 'reduce_storage_fees'
  | 'analyze_competitors'
  | 'create_promotion'
  | 'prepare_prime_day'
  | 'review_executive_dashboard';

export type TaskFlowPhase =
  | 'goal'
  | 'context'
  | 'ai_analysis'
  | 'recommendations'
  | 'simulation'
  | 'decision'
  | 'approval'
  | 'execution'
  | 'monitoring'
  | 'learning';

export type WorkflowType =
  | 'guided'
  | 'expert'
  | 'bulk'
  | 'ai_assisted'
  | 'review'
  | 'approval'
  | 'emergency'
  | 'collaborative';

export interface JourneyDefinition {
  id: string;
  label: string;
  description?: string;
  goals?: UserGoal[];
  steps?: string[];
  modules?: string[];
  workflow_type?: WorkflowType;
  estimated_minutes?: number;
}

export interface DecisionDesign {
  recommended_option: string;
  alternatives?: string[];
  evidence?: string[];
  expected_impact?: string;
  confidence: number;
  risk?: string;
  rollback_plan?: string;
}

export const PHILOSOPHIES: { id: Philosophy; label: string; description: string }[] = [
  { id: 'goal_first', label: 'Goal First', description: 'Entry points surface business goals, not module names' },
  { id: 'task_before_screen', label: 'Task Before Screen', description: 'Design tasks first; screens serve tasks' },
  { id: 'business_before_technology', label: 'Business Before Technology', description: 'Language reflects outcomes, not APIs' },
  { id: 'context_before_action', label: 'Context Before Action', description: 'Scope assembled before user acts' },
  { id: 'ai_assists_human_decides', label: 'AI Assists, Human Decides', description: 'AI recommends; human approves L3+' },
  { id: 'minimal_cognitive_load', label: 'Minimal Cognitive Load', description: 'Reduce clicks, typing, navigation, memory burden' },
  { id: 'progressive_disclosure', label: 'Progressive Disclosure', description: 'Summary first; detail on demand' },
  { id: 'explain_before_execute', label: 'Explain Before Execute', description: 'Preview impact before consequential actions' },
  { id: 'learn_through_doing', label: 'Learn Through Doing', description: 'Inline tips during task, not upfront manuals' },
  { id: 'predict_user_intent', label: 'Predict User Intent', description: 'Proactive next-step from context and history' },
];

export const USER_GOALS: { id: UserGoal; label: string }[] = [
  { id: 'launch_product', label: 'Launch Product' },
  { id: 'improve_listing', label: 'Improve Listing' },
  { id: 'increase_revenue', label: 'Increase Revenue' },
  { id: 'reduce_acos', label: 'Reduce ACOS' },
  { id: 'improve_roas', label: 'Improve ROAS' },
  { id: 'generate_images', label: 'Generate Images' },
  { id: 'publish_a_plus', label: 'Publish A+' },
  { id: 'fix_listing_errors', label: 'Fix Listing Errors' },
  { id: 'increase_reviews', label: 'Increase Reviews' },
  { id: 'improve_inventory', label: 'Improve Inventory' },
  { id: 'reduce_storage_fees', label: 'Reduce Storage Fees' },
  { id: 'analyze_competitors', label: 'Analyze Competitors' },
  { id: 'create_promotion', label: 'Create Promotion' },
  { id: 'prepare_prime_day', label: 'Prepare for Prime Day' },
  { id: 'review_executive_dashboard', label: 'Review Executive Dashboard' },
];

export const TASK_FLOW_PHASES: { id: TaskFlowPhase; label: string }[] = [
  { id: 'goal', label: 'Goal' },
  { id: 'context', label: 'Context' },
  { id: 'ai_analysis', label: 'AI Analysis' },
  { id: 'recommendations', label: 'Recommendations' },
  { id: 'simulation', label: 'Simulation' },
  { id: 'decision', label: 'Decision' },
  { id: 'approval', label: 'Approval' },
  { id: 'execution', label: 'Execution' },
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'learning', label: 'Learning' },
];

export const WORKFLOW_TYPES: { id: WorkflowType; label: string }[] = [
  { id: 'guided', label: 'Guided Workflow' },
  { id: 'expert', label: 'Expert Workflow' },
  { id: 'bulk', label: 'Bulk Workflow' },
  { id: 'ai_assisted', label: 'AI Assisted Workflow' },
  { id: 'review', label: 'Review Workflow' },
  { id: 'approval', label: 'Approval Workflow' },
  { id: 'emergency', label: 'Emergency Workflow' },
  { id: 'collaborative', label: 'Collaborative Workflow' },
];

export const JOURNEYS: JourneyDefinition[] = [
  { id: 'brand_onboarding', label: 'Brand Onboarding', description: 'First-time brand setup', steps: ['Register', 'Verify Email', 'Create Organization', 'Create Brand', 'Connect Amazon'], modules: ['auth', 'brand', 'amazonconnect'], workflow_type: 'guided', estimated_minutes: 30 },
  { id: 'listing_optimization', label: 'Listing Optimization', description: 'SEO and content improvement', goals: ['improve_listing', 'fix_listing_errors'], steps: ['Select Listing', 'AI Audit', 'Review Recommendations', 'Apply Changes', 'Publish'], modules: ['aicatalog', 'contentintel'], workflow_type: 'ai_assisted', estimated_minutes: 20 },
  { id: 'advertising_optimization', label: 'Advertising Optimization', description: 'Campaign and bid optimization', goals: ['increase_revenue', 'reduce_acos', 'improve_roas'], steps: ['Portfolio Review', 'AI Analysis', 'Simulate', 'Approve', 'Monitor'], modules: ['revenueops'], workflow_type: 'ai_assisted', estimated_minutes: 25 },
  { id: 'catalog_import', label: 'Catalog Import', description: 'Initial product sync', goals: ['launch_product'], steps: ['Connect', 'Select ASINs', 'Sync', 'Review'], modules: ['amazonsync'], workflow_type: 'ai_assisted', estimated_minutes: 45 },
  { id: 'executive_review', label: 'Executive Review', description: 'C-suite briefing', goals: ['review_executive_dashboard'], steps: ['Daily Briefing', 'KPI Review', 'AI Insights', 'Action Items'], modules: ['commandcenter'], workflow_type: 'guided', estimated_minutes: 15 },
  { id: 'settlement_review', label: 'Settlement Review', description: 'Finance reconciliation', steps: ['Import', 'Reconcile', 'Identify Leakages', 'File Claims'], modules: ['financeops'], workflow_type: 'review', estimated_minutes: 40 },
  { id: 'inventory_planning', label: 'Inventory Planning', description: 'Stock and replenishment', goals: ['improve_inventory'], steps: ['Review Stock', 'Forecast', 'Plan', 'Ship'], modules: ['inventoryintel'], workflow_type: 'expert', estimated_minutes: 30 },
  { id: 'image_generation', label: 'Image Generation', description: 'AI product imagery', goals: ['generate_images'], steps: ['Select Product', 'Brief', 'Generate', 'Review'], modules: ['aicreative'], workflow_type: 'ai_assisted', estimated_minutes: 15 },
];

export const ONBOARDING_MILESTONES = [
  'First Login',
  'Marketplace Connected',
  'First Product Import',
  'First AI Recommendation',
  'First Image Generation',
  'First Publication',
];

export const HX_METRICS = [
  { id: 'task_completion_time', label: 'Task Completion Time', direction: 'decrease' },
  { id: 'clicks_per_task', label: 'Clicks per Task', direction: 'decrease' },
  { id: 'ai_acceptance_rate', label: 'AI Acceptance Rate', direction: 'increase' },
  { id: 'recommendation_acceptance', label: 'Recommendation Acceptance', direction: 'increase' },
  { id: 'workflow_completion', label: 'Workflow Completion', direction: 'increase' },
  { id: 'user_satisfaction', label: 'User Satisfaction', direction: 'increase' },
  { id: 'time_to_value', label: 'Time to Value', direction: 'decrease' },
  { id: 'learning_curve', label: 'Learning Curve', direction: 'decrease' },
];

export const DEMO_DECISION: DecisionDesign = {
  recommended_option: 'Increase SP Brand Defense budget $150 → $180',
  alternatives: ['Increase bids 10%', 'Expand keywords first', 'Pause underperformers'],
  evidence: ['ROAS 6.73 over 30 days', '18% lost impression share', '94.9% budget utilization'],
  expected_impact: '+$4,200/month sales at current ROAS',
  confidence: 0.91,
  risk: 'ACOS may rise if conversion drops post-change',
  rollback_plan: 'Revert budget to $150; effective within 1 hour',
};

export function buildHXDashboard() {
  return {
    philosophies: PHILOSOPHIES.length,
    user_goals: USER_GOALS.length,
    journeys: JOURNEYS.length,
    task_flow_phases: TASK_FLOW_PHASES.length,
    workflow_types: WORKFLOW_TYPES.length,
    onboarding_milestones: ONBOARDING_MILESTONES.length,
    hx_metrics: HX_METRICS.length,
  };
}

export function goalLabel(goal: UserGoal): string {
  return USER_GOALS.find((g) => g.id === goal)?.label ?? goal.replace(/_/g, ' ');
}
